import React, { useEffect, useState } from "react";
import axios from "axios";
import { Download, FileText } from "lucide-react";
import { API } from "../../auth/AuthContext";

const money = (value, currency) => new Intl.NumberFormat(undefined, {
  style: "currency",
  currency,
  minimumFractionDigits: 2,
}).format(Number(value || 0));

export default function HostStatements({ requestedStatementId }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(`${API}/host/statements`, { withCredentials: true });
        if (!active) return;
        setItems(response.data?.items || []);
        if (requestedStatementId) {
          const detail = await axios.get(`${API}/host/statements/${requestedStatementId}`, { withCredentials: true });
          if (active) setSelected(detail.data);
        }
      } catch (err) {
        if (active) setError(err.response?.data?.detail || "Could not load statements");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [requestedStatementId]);

  const openStatement = async (statementId) => {
    if (selected?.statement_id === statementId) {
      setSelected(null);
      return;
    }
    setError("");
    try {
      const response = await axios.get(`${API}/host/statements/${statementId}`, { withCredentials: true });
      setSelected(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load statement");
    }
  };

  const download = async (statement) => {
    setDownloading(statement.statement_id);
    try {
      const response = await axios.get(
        `${API}/host/statements/${statement.statement_id}/download`,
        { withCredentials: true, responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `zcars-host-statement-${statement.period}-${statement.currency}-v${statement.version}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not download statement");
    } finally {
      setDownloading("");
    }
  };

  if (loading) return <div className="text-sm text-[var(--zc-text-3)]">Loading statements…</div>;

  return (
    <div className="space-y-6" data-testid="host-statements">
      <div>
        <div className="eyebrow mb-2">Monthly statements</div>
        <h2 className="zc-headline text-4xl md:text-5xl">Earnings history</h2>
        <p className="text-sm text-[var(--zc-text-2)] mt-2 max-w-2xl">
          Immutable monthly snapshots separate each currency and reconcile finalized car and parking payouts.
        </p>
      </div>
      {error && (
        <div className="rounded-xl border border-[var(--zc-danger)] p-4 text-sm text-[var(--zc-danger)]" role="alert">
          {error}
        </div>
      )}
      {items.length === 0 ? (
        <div className="zc-card p-8 text-center text-sm text-[var(--zc-text-3)]">
          No monthly statements are available yet.
        </div>
      ) : (
        <div className="zc-card overflow-hidden">
          {items.map((statement) => (
            <div key={statement.statement_id} className="border-b border-[var(--zc-border)] last:border-0">
              <div className="p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <button
                  type="button"
                  className="flex items-center gap-3 text-left min-w-0"
                  onClick={() => openStatement(statement.statement_id)}
                  aria-expanded={selected?.statement_id === statement.statement_id}
                >
                  <span className="w-10 h-10 rounded-xl bg-[var(--zc-accent-soft)] text-[var(--zc-accent)] grid place-items-center shrink-0">
                    <FileText size={18} />
                  </span>
                  <span className="min-w-0">
                    <span className="font-semibold block">{statement.period} · {statement.currency}</span>
                    <span className="text-xs text-[var(--zc-text-3)]">
                      Version {statement.version} · Closing {money(statement.totals?.closing_balance, statement.currency)}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="zc-btn-outline-primary h-10 px-4 text-sm inline-flex items-center justify-center gap-2"
                  onClick={() => download(statement)}
                  disabled={downloading === statement.statement_id}
                >
                  <Download size={14} /> {downloading === statement.statement_id ? "Preparing…" : "Download CSV"}
                </button>
              </div>
              {selected?.statement_id === statement.statement_id && (
                <StatementDetail statement={selected} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatementDetail({ statement }) {
  const totals = statement.totals || {};
  const cards = [
    ["Opening balance", totals.opening_balance],
    ["Car earnings", totals.car_earnings],
    ["Parking earnings", totals.parking_earnings],
    ["Fees", totals.fees],
    ["Refunds", totals.refunds],
    ["Adjustments", totals.adjustments],
    ["Payout credits", totals.payouts],
    ["Closing balance", totals.closing_balance],
  ];
  return (
    <div className="border-t border-[var(--zc-border)] bg-[var(--zc-bg-3)] p-4 sm:p-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(([label, value]) => (
          <div key={label} className="rounded-xl bg-[var(--zc-bg-2)] border border-[var(--zc-border)] p-4">
            <div className="eyebrow">{label}</div>
            <div className="zc-price text-lg mt-1">{money(value, statement.currency)}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 overflow-x-auto rounded-xl border border-[var(--zc-border)] bg-[var(--zc-bg-2)]">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="text-left text-xs uppercase tracking-wider text-[var(--zc-text-3)]">
            <tr className="border-b border-[var(--zc-border)]">
              <th className="p-3">Date</th><th className="p-3">Detail</th><th className="p-3">Source</th>
              <th className="p-3 text-right">Gross</th><th className="p-3 text-right">Fee</th><th className="p-3 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(statement.transactions || []).map((row) => (
              <tr key={`${row.transaction_id}-${row.type}`} className="border-b border-[var(--zc-border)] last:border-0">
                <td className="p-3 text-[var(--zc-text-2)]">{new Date(row.occurred_at).toLocaleDateString()}</td>
                <td className="p-3"><div className="font-medium">{row.label}</div><div className="zc-meta normal-case">{row.type}</div></td>
                <td className="p-3 capitalize">{row.source}</td>
                <td className="p-3 text-right">{money(row.gross_amount, statement.currency)}</td>
                <td className="p-3 text-right">{money(row.fee_amount, statement.currency)}</td>
                <td className="p-3 text-right font-semibold">{money(row.amount, statement.currency)}</td>
              </tr>
            ))}
            {!statement.transactions?.length && (
              <tr><td colSpan="6" className="p-6 text-center text-[var(--zc-text-3)]">No finalized activity this month.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[var(--zc-text-3)] mt-4">
        Escrow funding is excluded to avoid counting the same booking twice. Taxes and fractional-ownership values are not included because statement-grade source data is unavailable.
      </p>
    </div>
  );
}
